import { useEffect, useMemo, useRef, useState } from "react";
import type { FormEvent } from "react";
import { LANG_LABELS, LIMITS, TRANSLATION_LIMITS, allCategories, dishLangState, applyProofEdit, categorySpots, dishSource, dishSpots, textSpots, cleanTags, codesText, priceSpots, editsCatalog, findDishes, isDrink, keepsName, kindOf, lacksAmount, mlOf, nextNumber, parseCodes, parsePrice, plainPrice, sourceOf, suggestedAllergens, tagKey, topCategory, translationState, variantsText } from "@speisekarte/core";
import type { Dish, DishInput, DishKind, Lang, Translation, TranslationsInput } from "@speisekarte/core";
import { ApiError, api, messageOf, store, useStore } from "../store.ts";
import type { Data } from "../store.ts";
import { TagSymbol } from "../icon.tsx";
import { useWide } from "../wide.ts";
import { forget, useDraft, useKept } from "../kept.ts";
import { Toast, useToast } from "./Toast.tsx";
import { SymbolField } from "./Symbols.tsx";
import { ProofBox, openEdits, proofOn, stillChecked } from "./Proof.tsx";
import { CatalogTexts, PhraseLine, allDone, reviewHref, textsAction, textsOn, useTextStatus } from "./TextWork.tsx";
import { useModel } from "../model.ts";
import { WorkLang, useWorkLang } from "./LangChoice.tsx";
import type { OpenEdit } from "./Proof.tsx";

/**
 * Whether drinks are in play (S22): the business has a drink that is not archived. Without, the app
 * looks as it did before drinks, but for „Getränke“ under „Kataloge“.
 */
export const drinksInPlay = (data: Pick<Data, "dishes">) => data.dishes.some((d) => isDrink(d) && !d.archived);

/** Speisen | Getränke: which list a page of the catalogue shows (S22). */
export function KindSwitch({ kind, food, drink }: { kind: DishKind; food: string; drink: string }) {
  return (
    <div className="segmented small kind-switch" data-testid="kind-switch">
      <a className={`button ${kind === "food" ? "active" : ""}`} href={food} aria-current={kind === "food" || undefined}>
        Speisen
      </a>
      <a className={`button ${kind === "drink" ? "active" : ""}`} href={drink} aria-current={kind === "drink" || undefined} data-testid="kind-drink">
        Getränke
      </a>
    </div>
  );
}

/** Where the catalogue of a kind lives (S22). */
const routes = (kind: DishKind) => (kind === "drink" ? { list: "#/getraenke", one: "#/getraenk/", query: "?art=getraenke" } : { list: "#/kataloge", one: "#/speise/", query: "" });

/**
 * „Kataloge“ (the tab, once „Gerichte“): the catalogue of dishes, with search, filters and the form of
 * a dish. The drinks (S22) under „Getränke“, the same, with their own categories and tags.
 */

export function Dishes({ selected, kind = "food" }: { selected: string | null; kind?: DishKind }) {
  const s = useStore();
  const data = s.data!;
  const wide = useWide();
  const drink = kind === "drink";
  const at = routes(kind);
  const prefix = drink ? "drinks" : "dishes";
  const [q, setQ] = useKept(`${prefix}:q`, "");
  const [cat, setCat] = useKept<string | null>(`${prefix}:cat`, null);
  const [missing, setMissing] = useKept(`${prefix}:missing`, false);
  // Allergens the model guessed when an old menu was read (S41), until someone looked at them.
  const [unchecked, setUnchecked] = useKept(`${prefix}:unchecked`, false);
  const [archived, setArchived] = useKept(`${prefix}:archived`, false);
  // Only with a further language switched on (S36): „ohne Englisch“ like „ohne Allergene“.
  const langs = data.settings.languages ?? [];
  const [untranslated, setUntranslated] = useKept<Lang | null>(`${prefix}:untranslated`, null);
  // With several languages one at a time (01.11.2026): its filter, its button, its word in the rows.
  const [workLang, setWorkLang] = useWorkLang(langs);
  const open = (d: Dish, l: Lang) => translationState(d, l) !== "done";
  // The model's translation not looked through yet, or one out of date (S61).
  const [reviewOnly, setReviewOnly] = useKept(`${prefix}:review`, false);
  const toLook = (d: Dish, l: Lang) => ["review", "stale"].includes(dishLangState(d, l) ?? "");
  // „Deutsch prüfen“ (S47): the corrections still open, like „ohne Englisch“ only when switched on.
  const [proofFilter, setProofFilter] = useKept(`${prefix}:proof`, false);
  const proofing = proofOn(data);
  const suggested = (d: Dish) => openEdits(data, [...dishSpots(d), ...priceSpots(d)]).length;
  const canEdit = editsCatalog(s.role);
  const model = useModel();
  const catalogStatus = useTextStatus(data);
  const pool = useMemo(() => data.dishes.filter((d) => kindOf(d) === kind), [data.dishes, kind]);
  const order = drink ? data.drinkCategories : data.categories;
  const categories = useMemo(() => allCategories(pool, order), [pool, order]);
  const withoutAllergens = pool.filter((d) => !d.archived && d.allergens === null).length;
  const guessed = pool.filter((d) => !d.archived && d.allergensBy === "ai").length;
  const lang = untranslated && untranslated === workLang ? untranslated : null;
  const hits = findDishes(pool, q).filter((d) => (archived ? true : !d.archived) && (!cat || topCategory(d.category) === cat) && (!missing || d.allergens === null) && (!unchecked || d.allergensBy === "ai") && (!lang || open(d, lang)) && (!reviewOnly || !workLang || toLook(d, workLang)) && (!proofFilter || !proofing || suggested(d) > 0));
  const dish = selected && selected !== "neu" ? (pool.find((d) => d.id === selected) ?? null) : null;
  const form = selected ? <DishForm key={selected} dish={dish} kind={kind} canEdit={canEdit} onDone={() => (location.hash = at.list)} /> : null;

  const list = (
    <div>
      <div className="row wrap" style={{ marginBottom: "0.6rem" }}>
        {canEdit && (
          <a className="button primary" href={`${at.one}neu`} data-testid={drink ? "new-drink" : "new-dish"}>
            {drink ? "Neues Getränk" : "Neue Speise"}
          </a>
        )}
        <a className="button" href={drink ? "#/kataloge" : "#/getraenke"} data-testid={drink ? "to-dishes" : "to-drinks"}>
          {drink ? "Speisen" : "Getränke"}
        </a>
        <a className="button" href={`#/kategorien${at.query}`} data-testid="to-categories">
          Kategorien
          <ProofCount n={openEdits(data, categorySpots(order, kind)).length} />
        </a>
        <a className="button" href={`#/tags${at.query}`} data-testid="to-tags">
          Tags
        </a>
        <a className="button" href="#/textbausteine" data-testid="to-texts">
          Textbausteine
          <ProofCount n={openEdits(data, data.texts.flatMap(textSpots)).length} />
        </a>
        <a className="button" href="#/eigene-seiten" data-testid="to-own-pages">
          Eigene PDF-Seiten
        </a>
        <a className="button" href="#/allergene">
          Allergene & Zusatzstoffe
        </a>
        {textsOn(data) && (
          <a className="button" href={reviewHref(null, { flow: model && !allDone(catalogStatus) })} data-testid="to-phrases">
            {textsAction(data, model, catalogStatus)}
          </a>
        )}
        <a className="button" href="#/import" data-testid="to-import">
          Excel und Einlesen …
        </a>
      </div>
      {drink && canEdit && <DrinkStart />}
      {workLang && <WorkLang langs={langs} value={workLang} onChange={setWorkLang} />}
      <div className="filters-row">
        <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, Nummer, Tag …" aria-label={drink ? "Getränke suchen" : "Speisen suchen"} data-testid="dish-search" />
      </div>
      <div className="chips" style={{ marginBottom: "0.4rem" }}>
        {categories.map((c) => (
          <button key={c} className={cat === c ? "active" : ""} onClick={() => setCat(cat === c ? null : c)}>
            {c}
          </button>
        ))}
        {(withoutAllergens > 0 || missing) && (
          <button className={missing ? "active" : ""} onClick={() => setMissing(!missing)} data-testid="filter-missing">
            ohne Allergene ({withoutAllergens})
          </button>
        )}
        {(guessed > 0 || unchecked) && (
          <button className={unchecked ? "active" : ""} onClick={() => setUnchecked(!unchecked)} data-testid="filter-unchecked">
            Angaben ungeprüft ({guessed})
          </button>
        )}
        {(workLang ? [workLang] : []).map((l) => {
          const n = pool.filter((d) => !d.archived && open(d, l)).length;
          return n > 0 || lang === l ? (
            <button key={l} className={lang === l ? "active" : ""} onClick={() => setUntranslated(lang === l ? null : l)} data-testid={`filter-untranslated-${l}`}>
              ohne {LANG_LABELS[l]} ({n})
            </button>
          ) : null;
        })}
        {(workLang ? [workLang] : []).map((l) => {
          const n = pool.filter((d) => !d.archived && toLook(d, l)).length;
          return n > 0 || reviewOnly ? (
            <button key={`review-${l}`} className={reviewOnly ? "active" : ""} onClick={() => setReviewOnly(!reviewOnly)} data-testid={`filter-review-${l}`}>
              {LANG_LABELS[l]} nicht durchgesehen ({n})
            </button>
          ) : null;
        })}
        {proofing &&
          (() => {
            const n = pool.filter((d) => !d.archived && suggested(d) > 0).length;
            return n > 0 || proofFilter ? (
              <button className={proofFilter ? "active" : ""} onClick={() => setProofFilter(!proofFilter)} data-testid="filter-proof">
                Vorschläge ({n})
              </button>
            ) : null;
          })()}
        <button className={archived ? "active" : ""} onClick={() => setArchived(!archived)}>
          mit archivierten
        </button>
      </div>
      {/* The German and the languages (S61): where they stand, the way to check and translate. */}
      {textsOn(data) && <CatalogTexts data={data} />}
      {missing && withoutAllergens === 0 ? (
        // The filter is kept while one looks elsewhere; with nothing left to fill in it says so, not „0 Speisen“ (C3).
        <p className="empty" data-testid="allergens-done">
          Alle Allergene gepflegt 🎉{" "}
          <button className="link" onClick={() => setMissing(false)} data-testid="clear-missing">
            Filter aufheben
          </button>
        </p>
      ) : (
        <p className="muted small">{drink ? (hits.length === 1 ? "1 Getränk" : `${hits.length} Getränke`) : hits.length === 1 ? "1 Speise" : `${hits.length} Speisen`}</p>
      )}
      <ul className="list card" style={{ padding: "0 0.4rem" }}>
        {hits.map((d) => (
          <li key={d.id}>
            <a className={`dish-row ${d.id === selected ? "on" : ""} ${d.archived ? "archived" : ""}`} href={`${at.one}${d.id}`} data-testid={`dish-${d.number}`}>
              <span className="no">{d.number}</span>
              <span className="name">
                <b>{d.name}</b>
                {d.allergens?.length ? <sup className="codes">{d.allergens.join(",")}</sup> : null}
                {d.archived && <span className="tag soft">archiviert</span>}
                <span className="facts">{[d.category, d.tags.join(", "), drink && d.priceLabel ? d.priceLabel : "", variantsText(d.variants), d.allergens === null ? "Allergene fehlen" : d.allergensBy === "ai" ? "Angaben ungeprüft" : "", drink && lacksAmount(d) ? "Menge fehlt" : "", proofing && suggested(d) ? (suggested(d) === 1 ? "1 Vorschlag" : `${suggested(d)} Vorschläge`) : "", ...(workLang ? [workLang] : []).map((l) => ({ none: `${LANG_LABELS[l]} fehlt`, missing: `${LANG_LABELS[l]} unvollständig`, stale: `${LANG_LABELS[l]} veraltet`, done: dishLangState(d, l) === "review" ? `${LANG_LABELS[l]} nicht durchgesehen` : "" })[translationState(d, l)])].filter(Boolean).join(" · ")}</span>
              </span>
              <span className="price">{plainPrice(d.price)}</span>
            </a>
          </li>
        ))}
      </ul>
    </div>
  );

  if (wide) {
    return (
      <div className="split">
        {list}
        <div className="side-pane">{form ?? <p className="muted">{drink ? "Ein Getränk wählen, um es zu ändern." : "Eine Speise wählen, um sie zu ändern."}</p>}</div>
      </div>
    );
  }
  return form ?? list;
}

/** „Nummern neuer Getränke ab …“ (S22): where the numbers of new drinks begin. */
function DrinkStart() {
  const data = useStore().data!;
  const [value, setValue] = useState(String(data.settings.drinkStart));
  const [state, setState] = useState<{ ok?: string; error?: string }>({});
  useEffect(() => setValue(String(data.settings.drinkStart)), [data.settings.drinkStart]);
  async function save() {
    const n = Number(value);
    if (!Number.isInteger(n) || n < 1 || n > 99_999) return setState({ error: "Eine ganze Zahl von 1 bis 99999" });
    if (n === data.settings.drinkStart) return;
    try {
      await store.change(() => api("/api/settings", { method: "PUT", body: { drinkStart: n } }));
      setState({ ok: "Gespeichert" });
    } catch (err) {
      setState({ error: messageOf(err) });
    }
  }
  return (
    <label className="row drink-start" style={{ marginBottom: "0.6rem" }}>
      <span className="muted small">Nummern neuer Getränke ab</span>
      <input inputMode="numeric" value={value} onChange={(e) => [setValue(e.target.value), setState({})]} onBlur={() => void save()} onKeyDown={(e) => e.key === "Enter" && (e.currentTarget as HTMLInputElement).blur()} style={{ width: "6rem" }} data-testid="drink-start" />
      {state.error && <span className="error-text small">{state.error}</span>}
      {state.ok && <span className="ok-text small">{state.ok}</span>}
    </label>
  );
}

/** The form of a dish or a drink (S22): number, name, description, category, tags, allergens, price. */
function DishForm({ dish, kind, canEdit, onDone }: { dish: Dish | null; kind: DishKind; canEdit: boolean; onDone: () => void }) {
  const s = useStore();
  const data = s.data!;
  const toast = useToast();
  const drink = kind === "drink";
  const at = routes(kind);
  const categoryList = drink ? data.drinkCategories : data.categories;
  const tagList = drink ? data.drinkTags : data.tags;
  const maxVariants = drink ? LIMITS.drinkVariants : LIMITS.variants;
  // Kept while one looks elsewhere (S17); begins anew when the dish changed meanwhile.
  const draftKey = `${drink ? "drink" : "dish"}:${dish?.id ?? "neu"}`;
  const [form, setForm] = useDraft(draftKey, String(dish?.version ?? 0), () => ({
    number: String(dish?.number ?? nextNumber(data.dishes, kind, data.settings.drinkStart)),
    name: dish?.name ?? "",
    description: dish?.description ?? "",
    category: dish?.category ?? "",
    tags: dish?.tags.join(", ") ?? "",
    allergens: dish?.allergens ?? null,
    price: plainPrice(dish?.price ?? null),
    priceLabel: dish?.priceLabel ?? "",
    variants: (dish?.variants ?? []).map((v) => ({ label: v.label, price: plainPrice(v.price), codes: v.allergens.join(", ") })),
    archived: dish?.archived ?? false,
    // The further languages (S36): `fresh` once the translation was edited or confirmed against the German text.
    tr: Object.fromEntries((data.settings.languages ?? []).map((l) => [l, { name: dish?.translations?.[l]?.name ?? "", description: dish?.translations?.[l]?.description ?? "", source: dish?.translations?.[l]?.source ?? "", by: dish?.translations?.[l]?.by ?? ("hand" as Translation["by"]), fresh: false }])) as Partial<Record<Lang, TranslationField>>,
    keepName: dish?.keepName ?? null,
    // „Stimmt so“ at allergens the model guessed (S41).
    checked: false,
  }));
  const langs = data.settings.languages ?? [];
  // One language at a time (01.11.2026), the one chosen in the list; the others are kept as they are.
  const [workLang, setWorkLang] = useWorkLang(langs);
  const trOf = (l: Lang): TranslationField => form.tr?.[l] ?? { name: "", description: "", source: "", by: "hand", fresh: false };
  const setTr = (l: Lang, change: Partial<TranslationField>) => setForm((f) => ({ ...f, tr: { ...f.tr, [l]: { ...(f.tr?.[l] ?? trOf(l)), fresh: true, ...change } } }));
  const keepName = keepsName({ ...(drink ? { kind: "drink" as const } : {}), ...(form.keepName === null || form.keepName === undefined ? {} : { keepName: form.keepName }) });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const nameRef = useRef<HTMLInputElement>(null);
  const descRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => setError(null), [form]);
  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => setForm((f) => ({ ...f, [k]: v }));
  const codes = form.allergens ?? [];
  const known = new Set(data.allergens.map((a) => a.code));
  const setVariant = (k: number, change: Partial<(typeof form.variants)[number]>) => set("variants", form.variants.map((v, i) => (i === k ? { ...v, ...change } : v)));

  const done = () => {
    forget(draftKey);
    onDone();
  };

  // „Deutsch prüfen“ (S47): the corrections open in the name, description and price label as typed; taken into the field.
  // A further price's label is the key of its price on the menus (S19): that one under „Texte prüfen und übersetzen“, where it is renamed there too.
  const spots = dish ? [...dishSpots({ id: dish.id, name: form.name, description: form.description, ...(drink ? { kind: "drink" as const } : {}) }), ...priceSpots({ id: dish.id, priceLabel: form.priceLabel, variants: [] })] : [];
  const proofEdits = openEdits(data, spots);
  const take = (e: OpenEdit) => setForm((f) => (e.key.endsWith(":name") ? { ...f, name: applyProofEdit(f.name, e.edit) } : e.key.endsWith(":price") ? { ...f, priceLabel: applyProofEdit(f.priceLabel, e.edit) } : { ...f, description: applyProofEdit(f.description, e.edit) }));
  /** The German changed only by corrections taken: the sense is the same, a translation of it stays current. */
  const onlyCorrected = (): boolean => {
    if (!dish) return false;
    const same = (field: "name" | "description") => form[field] === dish[field] || (stillChecked(data, `dish:${dish.id}:${field}`, dish[field]) && stillChecked(data, `dish:${dish.id}:${field}`, form[field]));
    return same("name") && same("description");
  };

  async function submit(e: FormEvent) {
    e.preventDefault();
    const price = parsePrice(form.price);
    if ("error" in price) return setError(price.error);
    const number = Number(form.number);
    if (!Number.isInteger(number) || number < 1) return setError("Die Nummer muss eine ganze Zahl ab 1 sein.");
    const variants: NonNullable<DishInput["variants"]> = [];
    for (const v of form.variants) {
      if (!v.label.trim() && !v.price.trim() && !v.codes.trim()) continue;
      const p = parsePrice(v.price);
      if ("error" in p) return setError(`${v.label || "Weiterer Preis"}: ${p.error}`);
      const c = parseCodes(v.codes, known);
      if (c.unknown.length) return setError(`${v.label || "Weiterer Preis"}: unbekanntes Kürzel ${c.unknown.join(", ")}`);
      variants.push({ label: v.label, price: p.value, allergens: c.codes ?? [] });
    }
    const input: DishInput = { ...(drink ? { kind: "drink" as const } : {}), number, name: form.name, description: form.description, category: form.category, tags: cleanTags(form.tags), allergens: form.allergens, price: price.value, priceLabel: form.priceLabel, variants, archived: form.archived, ...(form.checked ? { allergensBy: null } : {}) };
    if (langs.length) {
      // Edited or confirmed: made from the German text as it is now; else from the one it was made from.
      const now = sourceOf(form.name, form.description);
      const translations: TranslationsInput = { ...dish?.translations };
      for (const l of langs) {
        const t = trOf(l);
        const current = dish && t.source === dishSource(dish) && onlyCorrected();
        translations[l] = { name: t.name, description: t.description, source: t.fresh || !t.source || current ? now : t.source, by: t.fresh ? "hand" : t.by };
      }
      input.translations = translations;
      input.keepName = form.keepName ?? null;
    }
    setBusy(true);
    try {
      if (dish) {
        try {
          await store.change(() => api(`/api/dishes/${dish.id}`, { method: "PUT", body: { ...input, version: dish.version } }));
        } catch (err) {
          if (!(err instanceof ApiError && err.code === "conflict")) throw err;
          if (!confirm(`${err.message} Deine Fassung trotzdem speichern?`)) return;
          await store.change(() => api(`/api/dishes/${dish.id}`, { method: "PUT", body: { ...input, version: null } }));
        }
      } else await store.change(() => api("/api/dishes", { body: input }));
      done();
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!dish || !confirm(`„${dish.name}“ entfernen? Steht es auf einer gespeicherten Speisekarte, wird es nur archiviert.`)) return;
    try {
      const r = await store.change(() => api<{ removed: boolean }>(`/api/dishes/${dish.id}`, { method: "DELETE" }));
      if (!r.removed) toast.show(drink ? "Archiviert: Das Getränk steht noch auf einer gespeicherten Speisekarte." : "Archiviert: Die Speise steht noch auf einer gespeicherten Speisekarte.");
      done();
    } catch (err) {
      setError(messageOf(err));
    }
  }

  return (
    <form className="card dish-form" noValidate onSubmit={submit} data-testid="dish-form">
      <div className="row between">
        <h2 style={{ margin: 0 }}>{dish ? dish.name : drink ? "Neues Getränk" : "Neue Speise"}</h2>
        <a href={at.list} className="button link" onClick={() => forget(draftKey)}>
          Schließen
        </a>
      </div>
      <fieldset disabled={!canEdit} style={{ border: "none", padding: 0, margin: 0 }}>
        <div className="two">
          <label>
            Nr.
            <input inputMode="numeric" value={form.number} onChange={(e) => set("number", e.target.value)} data-testid="dish-number" />
          </label>
          <label>
            Name
            <SymbolField target={nameRef} value={form.name} onChange={(v) => set("name", v)} disabled={!canEdit}>
              <input ref={nameRef} aria-label="Name" value={form.name} onChange={(e) => set("name", e.target.value)} maxLength={120} autoFocus={!dish} data-testid="dish-name" />
            </SymbolField>
          </label>
        </div>
        <label>
          Beschreibung
          <SymbolField target={descRef} value={form.description} onChange={(v) => set("description", v)} disabled={!canEdit}>
            <textarea ref={descRef} aria-label="Beschreibung" value={form.description} onChange={(e) => set("description", e.target.value)} maxLength={600} rows={3} placeholder="Ein bis zwei Sätze für die Gäste" data-testid="dish-description" />
          </SymbolField>
        </label>
        <ProofBox edits={proofEdits} take={take} canEdit={canEdit} />
        {workLang && <WorkLang langs={langs} value={workLang} onChange={setWorkLang} />}
        {(workLang ? [workLang] : []).map((l) => {
          const t = trOf(l);
          const state = translationState({ name: form.name, description: form.description, ...(keepName ? { keepName: true } : { keepName: false }), translations: { [l]: { ...t, source: t.fresh || !t.source || (dish && t.source === dishSource(dish) && onlyCorrected()) ? sourceOf(form.name, form.description) : t.source } } }, l);
          return (
            <fieldset className="translation" key={l} data-testid={`translation-${l}`}>
              <legend>
                {LANG_LABELS[l]}
                {t.by === "ai" && !t.fresh && (
                  <span className="tag soft" data-testid={`translation-ai-${l}`}>
                    {" "}
                    nicht durchgesehen{" "}
                    {state === "done" && (
                      // „Passt“ (S61): looked through, no longer to be checked; saved with the dish.
                      <button type="button" className="link inline" onClick={() => setForm((f) => ({ ...f, tr: { ...f.tr, [l]: { ...trOf(l), by: "checked" } } }))} data-testid={`translation-checked-${l}`}>
                        Passt
                      </button>
                    )}
                  </span>
                )}
              </legend>
              {state === "stale" && (
                <p className="warn small" data-testid={`translation-stale-${l}`}>
                  ⚠ Der deutsche Text hat sich geändert, seit das übersetzt wurde.{" "}
                  <button type="button" className="link" onClick={() => setTr(l, {})} data-testid={`translation-ok-${l}`}>
                    Passt so
                  </button>
                </p>
              )}
              <label className="check">
                <input type="checkbox" checked={keepName} onChange={(e) => set("keepName", e.target.checked)} data-testid="keep-name" />
                Name nicht übersetzen{drink ? " (bei Getränken üblich: Rioja, Estrella Galicia)" : " (etwa „Gambas Pil Pil“)"}
              </label>
              {!keepName && (
                <label>
                  Name
                  <input value={t.name} onChange={(e) => setTr(l, { name: e.target.value })} maxLength={TRANSLATION_LIMITS.name} placeholder={form.name} aria-label={`Name auf ${LANG_LABELS[l]}`} data-testid={`translation-name-${l}`} />
                </label>
              )}
              {form.description.trim() !== "" && (
                <label>
                  Beschreibung
                  <textarea value={t.description} onChange={(e) => setTr(l, { description: e.target.value })} maxLength={TRANSLATION_LIMITS.description} rows={2} placeholder={form.description} aria-label={`Beschreibung auf ${LANG_LABELS[l]}`} data-testid={`translation-description-${l}`} />
                </label>
              )}
            </fieldset>
          );
        })}
        <div className="two">
          <label>
            Preis €
            <input inputMode="decimal" value={form.price} onChange={(e) => set("price", e.target.value)} placeholder={drink ? "z. B. 3,80" : "z. B. 7,80"} data-testid="dish-price" />
          </label>
          <label>
            Kategorie
            <select
              value={form.category}
              onChange={(e) => {
                const category = e.target.value;
                // A new drink: the allergens its category suggests (S22), while none are entered.
                const suggested = drink && form.allergens === null ? suggestedAllergens(category, data.allergens) : null;
                setForm((f) => ({ ...f, category, ...(suggested ? { allergens: suggested } : {}) }));
              }}
              data-testid="dish-category"
            >
              <option value="">ohne Kategorie</option>
              {categoryList.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
              {/* A dish from before the list may name one that is gone; it shows until it is changed. */}
              {form.category && !categoryList.includes(form.category) && <option value={form.category}>{form.category} (nicht in der Liste)</option>}
            </select>
            {canEdit && (
              <a className="hint" href={`#/kategorien${at.query}`} style={{ display: "inline-block", marginTop: "0.25rem" }}>
                Kategorien bearbeiten …
              </a>
            )}
          </label>
        </div>
        <div className="variants" data-testid="dish-variants">
          <span className="muted small">{drink ? "Mengen und Preise" : "Mehrere Preise"}</span>
          <label>
            {drink ? "Menge zum Preis oben" : "Der Preis oben gilt für"}
            <input value={form.priceLabel} onChange={(e) => set("priceLabel", e.target.value)} maxLength={LIMITS.priceLabel} placeholder={drink ? "z. B. „0,3 l“, „4 cl“, „1/8 l“" : "frei lassen, oder z. B. „Ración“, „0,2 l“"} data-testid="dish-price-label" />
          </label>
          {/* Its translation right here (S61 § 4.1); one word for all dishes with it. */}
          {form.priceLabel === (dish?.priceLabel ?? "") && <PhraseLine text={form.priceLabel} data={data} />}
          {form.variants.map((v, k) => (
            <div className="variant-row" key={k}>
              <input value={v.label} onChange={(e) => setVariant(k, { label: e.target.value })} maxLength={LIMITS.priceLabel} placeholder={drink ? "z. B. 0,5 l, Flasche 0,75 l" : "z. B. ½ Ración, mit Feta"} aria-label={`Bezeichnung ${k + 1}`} data-testid={`variant-label-${k}`} />
              <input inputMode="decimal" value={v.price} onChange={(e) => setVariant(k, { price: e.target.value })} placeholder="Preis" aria-label={`Preis ${k + 1}`} data-testid={`variant-price-${k}`} />
              <input value={v.codes} onChange={(e) => setVariant(k, { codes: e.target.value })} placeholder="Kürzel" aria-label={`Zusätzliche Kürzel ${k + 1}`} title={drink ? "Kürzel, die nur hier dazukommen, etwa l für Sulfite" : "Kürzel, die nur hier dazukommen, etwa d für Thunfisch"} data-testid={`variant-codes-${k}`} />
              <button type="button" className="link" onClick={() => set("variants", form.variants.filter((_, i) => i !== k))} aria-label={`Preis ${v.label || k + 1} entfernen`}>
                ✕
              </button>
              {v.label === dish?.variants[k]?.label && (
                <div className="variant-tr">
                  <PhraseLine text={v.label} data={data} />
                </div>
              )}
            </div>
          ))}
          {form.variants.length < maxVariants && (
            <button type="button" onClick={() => set("variants", [...form.variants, { label: "", price: "", codes: "" }])} data-testid="add-variant">
              {drink ? "+ Weitere Menge" : "+ Weiterer Preis"}
            </button>
          )}
          <p className="hint">
            {drink
              ? "Zu jedem Preis gehört bei Getränken die Menge: „0,3 l“, „4 cl“, „1/8 l“, „Flasche 0,75 l“. Getränke mit denselben Mengen stehen auf der Speisekarte in Spalten untereinander."
              : form.priceLabel.trim() || form.variants.length
                ? "Jeder Preis steht in einer eigenen Zeile unter der Beschreibung. Kürzel verweisen auf Allergene und Zusatzstoffe und kommen zu denen der Speise dazu: z. B. „mit Thunfisch und Ei“ bekommt die Kürzel d, c."
                : "Für Portionen (Ración, ½ Ración), Größen (0,2 l, 0,4 l) oder Wahlmöglichkeiten (mit Feta, mit Thunfisch)."}
          </p>
          {drink && amountMissing(form) && (
            <p className="warn small" data-testid="amount-missing">
              ⚠ Ohne Menge: Die Preisangabenverordnung verlangt bei Getränken die Menge beim Preis.
            </p>
          )}
        </div>
        <div>
          <span className="muted small">Tags</span>
          <div className="chips" style={{ marginTop: "0.3rem" }} data-testid="dish-tags">
            {tagList.map((t) => {
              const on = cleanTags(form.tags).some((x) => tagKey(x) === tagKey(t));
              return (
                <button type="button" key={t} className={on ? "active" : ""} aria-pressed={on} onClick={() => set("tags", (on ? cleanTags(form.tags).filter((x) => tagKey(x) !== tagKey(t)) : [...cleanTags(form.tags), t]).join(", "))}>
                  {data.tagIcons[t] && <TagSymbol id={data.tagIcons[t]!} size={15} />} {t}
                </button>
              );
            })}
          </div>
          {editsCatalog(store.role) && (
            <a className="hint" href={`#/tags${at.query}`} style={{ display: "inline-block", marginTop: "0.25rem" }}>
              {tagList.length ? "Tags bearbeiten …" : "Noch keine Tags: anlegen …"}
            </a>
          )}
        </div>
        <div>
          <span className="muted small">Allergene und Zusatzstoffe</span>
          <div className="chips" style={{ marginTop: "0.3rem" }}>
            <button type="button" className={form.allergens === null ? "active" : ""} onClick={() => set("allergens", null)} data-testid="allergens-unknown">
              nicht gepflegt
            </button>
            <button type="button" className={form.allergens?.length === 0 ? "active" : ""} onClick={() => set("allergens", [])} data-testid="allergens-none">
              keine
            </button>
          </div>
          <div className="codes-grid">
            {data.allergens.map((a) => (
              <label key={a.code} className="check">
                <input type="checkbox" checked={codes.includes(a.code)} onChange={(e) => set("allergens", e.target.checked ? [...codes, a.code] : codes.filter((c) => c !== a.code))} data-testid={`code-${a.code}`} />
                <span>
                  <b>{a.code}</b> {a.name}
                </span>
              </label>
            ))}
          </div>
          {dish?.allergensBy === "ai" && !form.checked && codesText(form.allergens) === codesText(dish.allergens) && (
            <div className="notice" style={{ marginTop: "0.5rem" }} data-testid="allergens-guessed">
              {dish.tags.length ? "Allergene und Merkmale" : "Allergene"} vom Modell, nicht geprüft: Bitte mit Rezept und Lieferanten abgleichen.{" "}
              <button type="button" className="link" onClick={() => set("checked", true)} data-testid="allergens-checked">
                Stimmt so
              </button>
            </div>
          )}
          <p className="hint">{form.allergens === null ? `Noch nicht gepflegt: Die App zählt solche ${drink ? "Getränke" : "Speisen"}, damit nichts vergessen wird.` : form.allergens.length === 0 ? "Enthält keine der Allergene und Zusatzstoffe." : `Auf der Speisekarte: ${codesText(form.allergens)}`}</p>
        </div>
        {dish && (
          <label className="check">
            <input type="checkbox" checked={form.archived} onChange={(e) => set("archived", e.target.checked)} />
            Archiviert (nicht mehr zur Auswahl)
          </label>
        )}
      </fieldset>
      {error && <p className="error-box">{error}</p>}
      {canEdit ? (
        <div className="buttons">
          <button className="primary" disabled={busy || !form.name.trim()} data-testid="dish-save">
            {busy ? "Speichern …" : "Speichern"}
          </button>
          {dish && (
            <button type="button" className="danger" onClick={() => void remove()}>
              Entfernen
            </button>
          )}
        </div>
      ) : (
        <p className="hint">{drink ? "Getränke" : "Speisen"} ändern Inhaber und Verwaltung.</p>
      )}
      <Toast toast={toast} />
    </form>
  );
}

/** A translation as the form holds it (S36). */
type TranslationField = { name: string; description: string; source: string; by: Translation["by"]; fresh: boolean };

/** Whether a price of the form has no amount (S22): the main one with a price, or a further one. */
function amountMissing(form: { price: string; priceLabel: string; variants: { label: string; price: string }[] }): boolean {
  const named = (label: string) => mlOf(label) !== null || /\b(tasse|kännchen|pott|becher)\b/i.test(label);
  if (form.price.trim() && !named(form.priceLabel)) return true;
  return form.variants.some((v) => v.price.trim() !== "" && !named(v.label));
}

/** The number of corrections open behind a button (S47). */
function ProofCount({ n }: { n: number }) {
  return n > 0 ? (
    <span className="proof-n" title={n === 1 ? "Ein Vorschlag von „Deutsch prüfen“" : `${n} Vorschläge von „Deutsch prüfen“`} data-testid="proof-count">
      {n}
    </span>
  ) : null;
}
